import { useState, useEffect, useCallback, useRef } from 'react'
import { Upload, Trash2, FileText, Loader2, AlertCircle } from 'lucide-react'
import { businessService, BusinessDocument } from '@/services/businessService'
import { useToast } from '@/components/ui/MobileToast'

interface DocumentsTabProps {
  business: any
  onUpdate: (updated: any) => void
}

const DOCUMENT_TYPE_OPTIONS = [
  { value: 'registration_certificate', label: 'Registration Certificate' },
  { value: 'itr_copy', label: 'ITR Copy' },
  { value: 'gst_certificate', label: 'GST Certificate' },
  { value: 'pan_card', label: 'PAN Card' },
  { value: 'bank_statement', label: 'Bank Statement' },
  { value: 'other', label: 'Other' },
]

const ALLOWED_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png']
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function DocumentsTab({ business, onUpdate: _onUpdate }: DocumentsTabProps) {
  const { showToast } = useToast()
  const [documents, setDocuments] = useState<BusinessDocument[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showUploadForm, setShowUploadForm] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [documentType, setDocumentType] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const loadDocuments = useCallback(async () => {
    setIsLoading(true)
    try {
      const data = await businessService.getDocuments(business.id)
      setDocuments(data)
    } catch (err) {
      showToast({
        type: 'error',
        title: 'Load Failed',
        message: 'Unable to load documents.',
      })
    } finally {
      setIsLoading(false)
    }
  }, [business.id, showToast])

  useEffect(() => {
    loadDocuments()
  }, [loadDocuments])

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      return `Unsupported file type. Allowed: PDF, JPEG, PNG.`
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File too large (${formatFileSize(file.size)}). Maximum size is 10 MB.`
    }
    return null
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const error = validateFile(file)
    if (error) {
      setUploadError(error)
      setSelectedFile(null)
      return
    }

    setUploadError(null)
    setSelectedFile(file)
  }

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedFile || !documentType) {
      setUploadError('Please select a file and document type.')
      return
    }

    // Re-validate before upload
    const error = validateFile(selectedFile)
    if (error) {
      setUploadError(error)
      return
    }

    setIsUploading(true)
    setUploadError(null)

    try {
      // Step 1: Get presigned URL
      const { upload_url, s3_key } = await businessService.getUploadUrl(business.id, {
        file_name: selectedFile.name,
        document_type: documentType,
        mime_type: selectedFile.type,
        file_size_bytes: selectedFile.size,
      })

      // Step 2: Upload file directly to S3
      await fetch(upload_url, {
        method: 'PUT',
        body: selectedFile,
        headers: {
          'Content-Type': selectedFile.type,
        },
      })

      // Step 3: Confirm upload with backend
      const newDoc = await businessService.confirmUpload(business.id, {
        file_name: selectedFile.name,
        document_type: documentType,
        s3_key,
        file_size_bytes: selectedFile.size,
        mime_type: selectedFile.type,
      })

      // Reload documents list from API to ensure consistency
      await loadDocuments()
      setShowUploadForm(false)
      setSelectedFile(null)
      setDocumentType('')
      if (fileInputRef.current) fileInputRef.current.value = ''

      showToast({
        type: 'success',
        title: 'Upload Complete',
        message: `"${selectedFile.name}" uploaded successfully.`,
      })

      // Check for badge changes
      if ((newDoc as any).badge_awarded) {
        showToast({
          type: 'success',
          title: '🏆 Badge Earned!',
          message: `Congratulations! You earned the "${(newDoc as any).badge_awarded}" badge.`,
        })
      }
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Upload failed — please try again'
      setUploadError(message)
      showToast({ type: 'error', title: 'Upload Failed', message })
    } finally {
      setIsUploading(false)
    }
  }

  const handleDelete = async (docId: string) => {
    try {
      await businessService.deleteDocument(business.id, docId)
      setDocuments((prev) => prev.filter((d) => d.id !== docId))
      setDeleteConfirmId(null)
      showToast({ type: 'success', title: 'Deleted', message: 'Document deleted.' })
    } catch (err) {
      showToast({ type: 'error', title: 'Delete Failed', message: 'Unable to delete document.' })
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
        <span className="ml-2 text-gray-600 dark:text-gray-400">Loading documents...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Documents
        </h3>
        <button
          onClick={() => setShowUploadForm(true)}
          disabled={showUploadForm}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Upload className="w-4 h-4" />
          Upload Document
        </button>
      </div>

      {/* Upload Form */}
      {showUploadForm && (
        <form
          onSubmit={handleUpload}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
        >
          <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
            Upload Document
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Document Type <span className="text-red-500">*</span>
              </label>
              <select
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                required
              >
                <option value="">Select type...</option>
                {DOCUMENT_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                File <span className="text-red-500">*</span>
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileSelect}
                className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white text-sm file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:bg-primary-50 file:text-primary-700 dark:file:bg-primary-900/30 dark:file:text-primary-300"
                required
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Max 10 MB. Accepted: PDF, JPEG, PNG.
              </p>
            </div>
          </div>

          {uploadError && (
            <div className="mt-3 flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {uploadError}
            </div>
          )}

          {selectedFile && !uploadError && (
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
              Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
            </p>
          )}

          <div className="flex items-center gap-3 mt-4">
            <button
              type="submit"
              disabled={isUploading || !selectedFile || !documentType}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors disabled:opacity-50"
            >
              {isUploading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading...
                </span>
              ) : (
                'Upload'
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowUploadForm(false)
                setSelectedFile(null)
                setDocumentType('')
                setUploadError(null)
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Document List */}
      {documents.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
          <FileText className="w-10 h-10 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">
            No documents uploaded yet. Click "Upload Document" to get started.
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">
                  File Name
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">
                  Type
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">
                  Upload Date
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">
                  Size
                </th>
                <th className="px-4 py-3 text-right font-medium text-gray-600 dark:text-gray-300">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {documents.map((doc) => (
                <tr key={doc.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                  <td className="px-4 py-3 text-gray-900 dark:text-white">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="truncate max-w-[200px]">{doc.file_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400 capitalize">
                    {doc.document_type.replace(/_/g, ' ')}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                    {new Date(doc.created_at).toLocaleDateString('en-IN')}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                    {formatFileSize(doc.file_size_bytes)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {deleteConfirmId === doc.id ? (
                      <span className="inline-flex items-center gap-2">
                        <button
                          onClick={() => handleDelete(doc.id)}
                          className="px-2 py-1 text-xs text-white bg-red-600 hover:bg-red-700 rounded transition-colors"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setDeleteConfirmId(null)}
                          className="px-2 py-1 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                        >
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <button
                        onClick={() => setDeleteConfirmId(doc.id)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        aria-label={`Delete ${doc.file_name}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
